import { useEffect, useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DateRange } from 'react-day-picker'
import {
  TrendingUp,
  TrendingDown,
  Target,
  Trophy,
  XCircle,
  BarChart3,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Activity,
} from 'lucide-react'
import { EquityCurveChart } from '../charts/EquityCurveChart'
import { DateRangeFilter } from './DateRangeFilter'
import { Tooltip } from '@/components/ui/Tooltip'
import { apiService } from '@/services/api.service'
import { CalculationsService } from '@/services/calculations'
import { useAppStore } from '@/lib/store'
import { formatCurrency, formatPercent } from '@/lib/utils'

// Textos explicativos para tooltips das KPIs
const KPI_TOOLTIPS = {
  ativoMaisOperado: 'O ativo em que você realizou mais operações. Mostra a quantidade total de trades nesse ativo.',
  ativoMaisLucrativo: 'O ativo que trouxe mais lucro para você. Mostra o lucro total acumulado nesse ativo.',
  lucroTotal: 'Resultado líquido das suas operações (ganhos - perdas). Considera apenas trades finalizados com saldo real.',
  winRate: 'Porcentagem de operações vencedoras. Acima de 50% significa que você ganha mais vezes do que perde.',
  operacoes: 'Número total de trades que você realizou.',
  wins: 'Quantidade de operações que você ganhou.',
  losses: 'Quantidade de operações que você perdeu.',
  profitFactor: 'Relação entre ganhos e perdas totais. Acima de 1.0 significa que você ganha mais dinheiro do que perde.',
  mediaGanho: 'Valor médio que você ganha em cada operação vencedora.',
  mediaPerda: 'Valor médio que você perde em cada operação perdedora.',
}

// Componente de ícone de info com tooltip
function InfoTooltip({ content }: { content: string }) {
  return (
    <Tooltip content={content} side="top" delayDuration={100}>
      <button type="button" className="ml-1 inline-flex items-center text-muted-foreground/50 hover:text-muted-foreground transition-colors">
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  )
}

export function Dashboard() {
  const { trades, setTrades, settings, setKPIs, setLastSync } = useAppStore()
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  // Fetch trades from API
  const { data: fetchedTrades, isLoading: tradesLoading } = useQuery({
    queryKey: ['trades'],
    queryFn: () => apiService.fetchTrades(),
    refetchInterval: 5 * 60 * 1000,
  })

  // Fetch balance from API
  const { data: currentBalance, isLoading: balanceLoading } = useQuery({
    queryKey: ['balance'],
    queryFn: () => apiService.fetchBalance(),
    refetchInterval: 5 * 60 * 1000,
  })

  // Atualizar trades no store quando buscar da API
  useEffect(() => {
    if (fetchedTrades) {
      setTrades(fetchedTrades)
      setLastSync(new Date().toISOString())

      const calculatedKPIs = CalculationsService.calculateKPIs(
        fetchedTrades,
        settings.initial_balance,
      )

      if (currentBalance !== undefined) {
        calculatedKPIs.current_balance = currentBalance
      }

      setKPIs(calculatedKPIs)
    }
  }, [fetchedTrades, currentBalance, settings.initial_balance, setTrades, setKPIs, setLastSync])

  const isLoading = tradesLoading || balanceLoading

  // Filtrar trades pelo período selecionado
  const filteredTrades = useMemo(() => {
    if (!trades || !dateRange?.from) return trades

    const startOfDay = (date: Date) => {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    }
    const endOfDay = (date: Date) => {
      const d = new Date(date)
      d.setHours(23, 59, 59, 999)
      return d
    }

    const from = startOfDay(dateRange.from)
    const to = dateRange.to ? endOfDay(dateRange.to) : endOfDay(dateRange.from)

    return trades.filter(trade => {
      const tradeDate = new Date(trade.created_at)
      return tradeDate >= from && tradeDate <= to
    })
  }, [trades, dateRange])

  // Calcular KPIs baseado nos trades filtrados
  const filteredKPIs = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) {
      return {
        total_trades: 0,
        winning_trades: 0,
        losing_trades: 0,
        win_rate: 0,
        total_profit: 0,
        total_loss: 0,
        profit_factor: 0,
        avg_win: 0,
        avg_loss: 0,
      }
    }
    return CalculationsService.calculateKPIs(filteredTrades, settings.initial_balance)
  }, [filteredTrades, settings.initial_balance])

  // Asset stats calculados a partir dos trades filtrados
  const assetStats = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) return []
    return CalculationsService.calculateAssetStats(filteredTrades)
  }, [filteredTrades])

  // Ativo mais operado (mais trades) e ativo mais lucrativo
  const mostTradedAsset = assetStats.length > 0 ? assetStats[0] : null // já ordenado por total_trades desc
  const mostProfitableAsset = assetStats.length > 0
    ? [...assetStats].sort((a, b) => b.total_profit - a.total_profit)[0]
    : null

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    )
  }

  // Lucro das operações = total ganho - total perdido (baseado em trades filtrados)
  const lucroOperacoes = (filteredKPIs?.total_profit || 0) - (filteredKPIs?.total_loss || 0)
  // ROI das operações (quanto % de retorno sobre o total apostado/operado)
  const totalOperado = (filteredKPIs?.total_profit || 0) + (filteredKPIs?.total_loss || 0)
  const roiOperacoes = totalOperado > 0 ? (lucroOperacoes / totalOperado) * 100 : 0

  return (
    <div className="space-y-6">
      {/* FILTRO DE PERÍODO */}
      <div className="flex items-center justify-between">
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        {dateRange?.from && (
          <span className="text-sm text-muted-foreground">
            {filteredTrades?.length || 0} operações no período
          </span>
        )}
      </div>

      {/* SEÇÃO: SALDO E CAPITAL */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Visão Geral</h2>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Ativo Mais Operado */}
          <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 transition-all hover:border-border hover:shadow-lg">
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Activity className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Ativo Mais Operado
                    <InfoTooltip content={KPI_TOOLTIPS.ativoMaisOperado} />
                  </p>
                  <p className="text-2xl font-bold text-foreground truncate">
                    {mostTradedAsset?.symbol || '—'}
                  </p>
                  {mostTradedAsset && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {mostTradedAsset.total_trades} operações
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Ativo Mais Lucrativo */}
          <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 transition-all hover:border-border hover:shadow-lg">
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Ativo Mais Lucrativo
                    <InfoTooltip content={KPI_TOOLTIPS.ativoMaisLucrativo} />
                  </p>
                  <p className="text-2xl font-bold text-foreground truncate">
                    {mostProfitableAsset?.symbol || '—'}
                  </p>
                  {mostProfitableAsset && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {mostProfitableAsset.total_profit >= 0 ? '+' : ''}{formatCurrency(mostProfitableAsset.total_profit)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Lucro/Prejuízo das Operações */}
          <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 transition-all hover:border-border hover:shadow-lg">
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  {lucroOperacoes >= 0 ? (
                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                  ) : (
                    <TrendingDown className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Lucro (Operações)
                    <InfoTooltip content={KPI_TOOLTIPS.lucroTotal} />
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {lucroOperacoes >= 0 ? '+' : ''}{formatCurrency(lucroOperacoes)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {roiOperacoes >= 0 ? '+' : ''}{roiOperacoes.toFixed(1)}% ROI
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Win Rate */}
          <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 transition-all hover:border-border hover:shadow-lg">
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Target className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Win Rate
                    <InfoTooltip content={KPI_TOOLTIPS.winRate} />
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatPercent(filteredKPIs?.win_rate || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {filteredKPIs?.winning_trades || 0} de {filteredKPIs?.total_trades || 0} operações
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEÇÃO: MÉTRICAS DE PERFORMANCE */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Performance</h2>

        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
          {/* Total Operações */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Operações
                <InfoTooltip content={KPI_TOOLTIPS.operacoes} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{filteredKPIs?.total_trades || 0}</p>
          </div>

          {/* Wins */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Wins
                <InfoTooltip content={KPI_TOOLTIPS.wins} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{filteredKPIs?.winning_trades || 0}</p>
          </div>

          {/* Losses */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Losses
                <InfoTooltip content={KPI_TOOLTIPS.losses} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{filteredKPIs?.losing_trades || 0}</p>
          </div>

          {/* Profit Factor */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Profit Factor
                <InfoTooltip content={KPI_TOOLTIPS.profitFactor} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {(filteredKPIs?.profit_factor || 0).toFixed(2)}
            </p>
          </div>

          {/* Média Ganho */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Média Ganho
                <InfoTooltip content={KPI_TOOLTIPS.mediaGanho} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(filteredKPIs?.avg_win || 0)}</p>
          </div>

          {/* Média Perda */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4 transition-all hover:border-border hover:shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground flex items-center">
                Média Perda
                <InfoTooltip content={KPI_TOOLTIPS.mediaPerda} />
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(Math.abs(filteredKPIs?.avg_loss || 0))}</p>
          </div>
        </div>
      </div>

      {/* SEÇÃO: CURVA DE EQUITY */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Curva de Equity</h2>

        <EquityCurveChart trades={filteredTrades || []} currentBalance={currentBalance || 0} />
      </div>
    </div>
  )
}
